import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, CheckCircle2, XCircle, Loader2, ShieldAlert, ShieldCheck, ListChecks } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';

const FIELD = 'text-sm font-medium text-slate-500';
const VALUE = 'text-base font-mono text-slate-900 break-all';

export default function WhatsAppTest() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [cfg, setCfg] = useState(null);
  const [cfgError, setCfgError] = useState(null);

  useEffect(() => {
    api.get('/admin/diagnostics/whatsapp/config-status')
      .then(({ data }) => setCfg(data))
      .catch((e) => setCfgError(String(e?.response?.data?.detail || e?.message || 'Request failed')));
  }, []);

  const [mgmtLoading, setMgmtLoading] = useState(false);
  const [mgmtResult, setMgmtResult] = useState(null);
  const [mgmtError, setMgmtError] = useState(null);

  const sendTest = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await api.post('/admin/diagnostics/whatsapp/send-test');
      setResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Request failed';
      if (detail === 'WHATSAPP_PRODUCTION_CONFIGURATION_ERROR') {
        setError('WHATSAPP PRODUCTION CONFIGURATION ERROR — the production WhatsApp access token / phone number ID is not configured. No message was sent (production never falls back to a simulated send).');
      } else if (detail === 'WHATSAPP_NOT_ENABLED') {
        setError('WhatsApp is not enabled in this environment (WHATSAPP_ENABLED is off).');
      } else {
        setError(String(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const runManagementTest = async () => {
    setMgmtLoading(true);
    setMgmtResult(null);
    setMgmtError(null);
    try {
      const res = await api.get('/admin/diagnostics/whatsapp/management-api-test');
      setMgmtResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Request failed';
      if (detail === 'WHATSAPP_MANAGEMENT_NOT_CONFIGURED') {
        setMgmtError('WHATSAPP MANAGEMENT NOT CONFIGURED — WHATSAPP_ACCESS_TOKEN and/or WHATSAPP_BUSINESS_ACCOUNT_ID are not set in this environment. This read-only test can only run where the live Meta credentials exist (production).');
      } else {
        setMgmtError(String(detail));
      }
    } finally {
      setMgmtLoading(false);
    }
  };

  const [coexLoading, setCoexLoading] = useState(false);
  const [coexResult, setCoexResult] = useState(null);
  const [coexError, setCoexError] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subResult, setSubResult] = useState(null);
  const [subError, setSubError] = useState(null);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeResult, setSubscribeResult] = useState(null);
  const [subscribeError, setSubscribeError] = useState(null);

  const runDiag = async (path, setLoading, setResult, setError) => {
    setLoading(true); setResult(null); setError(null);
    try {
      const res = await api.get(path);
      setResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(String(detail).includes('NOT_CONFIGURED')
        ? 'NOT CONFIGURED in this environment — this read-only check can only run where the live Meta credentials exist (production).'
        : String(detail));
    } finally {
      setLoading(false);
    }
  };

  // Explicit, state-changing admin action (never run on page load):
  // POST /{WABA_ID}/subscribed_apps. On success the server re-runs the
  // read-only check and returns the resulting subscription state.
  const subscribeApp = async () => {
    setSubscribeLoading(true); setSubscribeResult(null); setSubscribeError(null);
    try {
      const res = await api.post('/admin/diagnostics/whatsapp/waba-subscribe');
      setSubscribeResult(res.data);
      if (res.data?.subscription) setSubResult(res.data.subscription);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Request failed';
      setSubscribeError(String(detail).includes('NOT_CONFIGURED')
        ? 'NOT CONFIGURED in this environment — this action can only run where the live Meta credentials exist (production).'
        : String(detail));
    } finally {
      setSubscribeLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="whatsapp-test-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-emerald-100">
          <MessageCircle className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">WhatsApp Test</h1>
          <p className="text-sm text-slate-500">
            Temporary production diagnostic — Super Admin only. Sends one template message via the live Meta Cloud API.
          </p>
        </div>
        <Badge className="ml-auto bg-amber-500 text-white" data-testid="wa-test-temp-badge">TEMPORARY</Badge>
      </div>

      <Card className="border-2 border-slate-300 mb-6" data-testid="wa-boundary-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-slate-700" />
            Meta App Boundary — Dedicated WhatsApp App
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Presence-only configuration status plus an authoritative Graph
            <span className="font-mono"> debug_token </span>
            check of which Meta App owns the configured access token. No secret or token value is ever shown.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {cfgError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="wa-boundary-error">
              <ShieldAlert className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="text-sm text-red-700">{cfgError}</div>
            </div>
          )}
          {cfg && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
                <div>
                  <div className={FIELD}>Dedicated WhatsApp App ID</div>
                  <div className={VALUE} data-testid="wa-boundary-app-id">
                    {cfg.whatsapp_app_id || 'not configured'}
                  </div>
                </div>
                <div>
                  <div className={FIELD}>Dedicated WhatsApp Config ID</div>
                  <div className={VALUE} data-testid="wa-boundary-config-id">
                    {cfg.whatsapp_config_id || 'not configured'}
                  </div>
                </div>
                <div>
                  <div className={FIELD}>Graph API Version</div>
                  <div className={VALUE} data-testid="wa-boundary-graph-version">{cfg.graph_version}</div>
                </div>
                <div>
                  <div className={FIELD}>WABA</div>
                  <div className={VALUE} data-testid="wa-boundary-waba">{cfg.waba_masked}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="wa-boundary-flags">
                {[
                  ['WhatsApp App ID', 'whatsapp_app_id'],
                  ['WhatsApp App Secret', 'whatsapp_app_secret'],
                  ['WhatsApp Config ID', 'whatsapp_config_id'],
                  ['Access Token', 'access_token'],
                  ['WABA ID', 'waba_id'],
                  ['Phone Number ID', 'phone_number_id'],
                ].map(([label, key]) => (
                  <div key={key} className="flex items-center gap-2 text-sm" data-testid={`wa-boundary-flag-${key}`}>
                    {cfg.configured?.[key]
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                    <span className="text-slate-600 truncate">{label}</span>
                    <span className="ml-auto font-mono text-xs text-slate-500">
                      {cfg.configured?.[key] ? 'configured' : 'not configured'}
                    </span>
                  </div>
                ))}
              </div>

              <div
                className={`p-3 rounded-lg border ${
                  cfg.token_app_match === 'YES' ? 'bg-emerald-50 border-emerald-200'
                    : cfg.token_app_match === 'NO' ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'}`}
                data-testid="wa-boundary-token-match"
              >
                <div className="font-semibold text-sm" data-testid="wa-boundary-token-match-value">
                  TOKEN APP MATCH: {cfg.token_app_match}
                </div>
                {cfg.token_app_match_reason && (
                  <div className="text-xs text-slate-600 mt-1" data-testid="wa-boundary-token-match-reason">
                    {cfg.token_app_match_reason}
                  </div>
                )}
                {cfg.token_app_match !== 'YES' && (
                  <div className="text-xs font-semibold text-red-700 mt-2" data-testid="wa-boundary-token-warning">
                    Do NOT treat the API tests below as App Review evidence for the dedicated WhatsApp app
                    until TOKEN APP MATCH is YES — the calls would be credited to a different Meta app.
                  </div>
                )}
                {cfg.meta_diagnostic && (
                  <div className="text-xs font-mono text-slate-600 mt-2 break-all" data-testid="wa-boundary-meta-diagnostic">
                    Meta diagnostic — HTTP {String(cfg.meta_diagnostic.http_status ?? '—')}
                    {' · code '}{String(cfg.meta_diagnostic.code ?? '—')}
                    {' · type '}{String(cfg.meta_diagnostic.type ?? '—')}
                    {' · subcode '}{String(cfg.meta_diagnostic.error_subcode ?? '—')}
                    {' · fbtrace '}{String(cfg.meta_diagnostic.fbtrace_id ?? '—')}
                    {cfg.meta_diagnostic.message ? ` · ${cfg.meta_diagnostic.message}` : ''}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-2 border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Controlled Test Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
            <div>
              <div className={FIELD}>Sender (read-only)</div>
              <div className={VALUE} data-testid="wa-test-sender">+90 531 638 82 95</div>
            </div>
            <div>
              <div className={FIELD}>Recipient (fixed)</div>
              <div className={VALUE} data-testid="wa-test-recipient">+90 531 250 63 00</div>
            </div>
            <div>
              <div className={FIELD}>Template</div>
              <div className={VALUE} data-testid="wa-test-template">hello_world</div>
            </div>
            <div>
              <div className={FIELD}>Language</div>
              <div className={VALUE} data-testid="wa-test-language">en_US</div>
            </div>
          </div>

          <Button
            onClick={sendTest}
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="wa-test-send-btn"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4 mr-2" /> Send Test Message</>
            )}
          </Button>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="wa-test-error">
              <ShieldAlert className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {result && (
            <div
              className={`p-4 rounded-lg border ${result.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
              data-testid="wa-test-result"
            >
              <div className="flex items-center gap-2 mb-3">
                {result.success ? (
                  <><CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-700" data-testid="wa-test-result-status">
                      META SEND ACCEPTED{result.mock ? ' (MOCK — no live token in this environment)' : ''}
                    </span></>
                ) : (
                  <><XCircle className="h-5 w-5 text-red-600" />
                    <span className="font-semibold text-red-700" data-testid="wa-test-result-status">META SEND FAILED</span></>
                )}
              </div>

              <dl className="grid grid-cols-3 gap-y-1 text-sm">
                <dt className={FIELD}>HTTP status</dt>
                <dd className="col-span-2 font-mono" data-testid="wa-test-http-status">{String(result.http_status ?? '—')}</dd>

                {result.message_id && (
                  <>
                    <dt className={FIELD}>Message ID</dt>
                    <dd className="col-span-2 font-mono break-all" data-testid="wa-test-wamid">{result.message_id}</dd>
                  </>
                )}

                {result.meta_error && (
                  <>
                    <dt className={FIELD}>Error code</dt>
                    <dd className="col-span-2 font-mono" data-testid="wa-test-err-code">{String(result.meta_error.code ?? '—')}</dd>
                    <dt className={FIELD}>Error subcode</dt>
                    <dd className="col-span-2 font-mono" data-testid="wa-test-err-subcode">{String(result.meta_error.subcode ?? '—')}</dd>
                    <dt className={FIELD}>Error type</dt>
                    <dd className="col-span-2 font-mono" data-testid="wa-test-err-type">{String(result.meta_error.type ?? '—')}</dd>
                    <dt className={FIELD}>Error message</dt>
                    <dd className="col-span-2" data-testid="wa-test-err-message">{String(result.meta_error.message ?? '—')}</dd>
                  </>
                )}

                <dt className={FIELD}>Sender ID</dt>
                <dd className="col-span-2 font-mono break-all" data-testid="wa-test-sender-id">{result.sender_phone_number_id || '—'}</dd>

                <dt className={FIELD}>App Review evidence</dt>
                <dd className="col-span-2 font-mono" data-testid="wa-test-app-review-evidence">
                  {result.app_review_evidence ? 'VALID (real Meta send)' : 'NOT VALID (mock or failed send)'}
                </dd>
              </dl>

              {result.success && (
                <p className="text-xs text-slate-500 mt-3">
                  Note: HTTP 200 only confirms Meta accepted the request. Delivery to the device is confirmed separately via the delivery/status webhook.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-2 border-slate-200 mt-6" data-testid="wa-mgmt-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
            Meta App Review — Management API Test
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Executes the exact read-only <span className="font-mono">GET /&#123;WABA_ID&#125;/phone_numbers</span> call
            required by Meta App Review for the <span className="font-mono">whatsapp_business_management</span> permission.
            Runs entirely server-side; no token is ever sent to the browser.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
            <div>
              <div className={FIELD}>WABA</div>
              <div className={VALUE} data-testid="wa-mgmt-waba">{mgmtResult?.waba_masked || '—'}</div>
            </div>
            <div>
              <div className={FIELD}>Graph Version</div>
              <div className={VALUE} data-testid="wa-mgmt-graph-version">{mgmtResult?.graph_version || '—'}</div>
            </div>
          </div>

          <Button
            onClick={runManagementTest}
            disabled={mgmtLoading}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            data-testid="wa-mgmt-run-btn"
          >
            {mgmtLoading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
            ) : (
              <><ListChecks className="h-4 w-4 mr-2" /> Run Management API Test</>
            )}
          </Button>

          {mgmtError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="wa-mgmt-error">
              <ShieldAlert className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="text-sm text-red-700">{mgmtError}</div>
            </div>
          )}

          {mgmtResult && (
            <div
              className={`p-4 rounded-lg border ${mgmtResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
              data-testid="wa-mgmt-result"
            >
              <div className="flex items-center gap-2 mb-3">
                {mgmtResult.success ? (
                  <><CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-700" data-testid="wa-mgmt-result-status">SUCCESS</span></>
                ) : (
                  <><XCircle className="h-5 w-5 text-red-600" />
                    <span className="font-semibold text-red-700" data-testid="wa-mgmt-result-status">FAILED</span></>
                )}
              </div>

              <dl className="grid grid-cols-3 gap-y-1 text-sm">
                <dt className={FIELD}>HTTP Status</dt>
                <dd className="col-span-2 font-mono" data-testid="wa-mgmt-http-status">
                  {String(mgmtResult.http_status ?? '—')}{mgmtResult.http_status === 200 ? ' OK' : ''}
                </dd>

                <dt className={FIELD}>Endpoint</dt>
                <dd className="col-span-2 font-mono break-all" data-testid="wa-mgmt-endpoint">{mgmtResult.endpoint || '—'}</dd>

                <dt className={FIELD}>Phone records</dt>
                <dd className="col-span-2 font-mono" data-testid="wa-mgmt-count">{String(mgmtResult.phone_number_count ?? 0)}</dd>

                {mgmtResult.meta_error && (
                  <>
                    <dt className={FIELD}>Error code</dt>
                    <dd className="col-span-2 font-mono" data-testid="wa-mgmt-err-code">{String(mgmtResult.meta_error.code ?? '—')}</dd>
                    <dt className={FIELD}>Error message</dt>
                    <dd className="col-span-2" data-testid="wa-mgmt-err-message">{String(mgmtResult.meta_error.message ?? mgmtResult.meta_error.transport_error ?? '—')}</dd>
                  </>
                )}
              </dl>

              {mgmtResult.success && mgmtResult.phone_numbers?.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="wa-mgmt-phone-list">
                  {mgmtResult.phone_numbers.map((p, i) => (
                    <div key={i} className="p-3 rounded-md bg-white border border-emerald-200 text-sm" data-testid={`wa-mgmt-phone-${i}`}>
                      <dl className="grid grid-cols-3 gap-y-1">
                        <dt className={FIELD}>Display number</dt>
                        <dd className="col-span-2 font-mono">{p.display_phone_number || '—'}</dd>
                        <dt className={FIELD}>Verified name</dt>
                        <dd className="col-span-2">{p.verified_name || '—'}</dd>
                        <dt className={FIELD}>Quality rating</dt>
                        <dd className="col-span-2">{p.quality_rating || '—'}</dd>
                        <dt className={FIELD}>Phone number ID</dt>
                        <dd className="col-span-2 font-mono break-all">{p.phone_number_id || '—'}</dd>
                      </dl>
                    </div>
                  ))}
                </div>
              )}

              {mgmtResult.success && (
                <p className="text-xs text-slate-500 mt-3">
                  A successful HTTP 200 satisfies Meta App Review's required <span className="font-mono">whatsapp_business_management</span> API test.
                  Meta may take up to 24 hours to reflect completed test activity — do not re-run repeatedly if the counter has not updated yet.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-2 border-slate-200 mt-6" data-testid="wa-coex-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sky-600" />
            Coexistence — Phone Status (read-only)
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Server-side <span className="font-mono">GET /&#123;PHONE_NUMBER_ID&#125;?fields=is_on_biz_app,platform_type</span>.
            Verifies what Meta reports for the configured phone. No state change; no token exposed.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={() => runDiag('/admin/diagnostics/whatsapp/phone-coexistence', setCoexLoading, setCoexResult, setCoexError)}
            disabled={coexLoading} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="wa-coex-run-btn">
            {coexLoading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking…</>) : (<><ListChecks className="h-4 w-4 mr-2" /> Check Phone Coexistence</>)}
          </Button>
          {coexError && (<div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="wa-coex-error"><ShieldAlert className="h-5 w-5 text-red-500 mt-0.5" /><div className="text-sm text-red-700">{coexError}</div></div>)}
          {coexResult && (
            <dl className="grid grid-cols-3 gap-y-1 text-sm p-4 rounded-lg border bg-slate-50" data-testid="wa-coex-result">
              <dt className={FIELD}>HTTP</dt><dd className="col-span-2 font-mono" data-testid="wa-coex-http">{String(coexResult.http_status ?? '—')}</dd>
              <dt className={FIELD}>phone_number_id</dt><dd className="col-span-2 font-mono break-all">{coexResult.phone_number_id || '—'}</dd>
              <dt className={FIELD}>is_on_biz_app</dt><dd className="col-span-2 font-mono">{String(coexResult.is_on_biz_app)}</dd>
              <dt className={FIELD}>platform_type</dt><dd className="col-span-2 font-mono">{String(coexResult.platform_type)}</dd>
              <dt className={FIELD}>Graph</dt><dd className="col-span-2 font-mono">{coexResult.graph_version}</dd>
              {coexResult.meta_error && (<><dt className={FIELD}>Error</dt><dd className="col-span-2">{coexResult.meta_error.code}: {coexResult.meta_error.message}</dd></>)}
            </dl>
          )}
        </CardContent>
      </Card>

      <Card className="border-2 border-slate-200 mt-6" data-testid="wa-sub-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-violet-600" />
            WABA Subscription
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Read-only check: <span className="font-mono">GET /&#123;WABA_ID&#125;/subscribed_apps</span>.
            Verifies the TreatX app is subscribed to the WABA. No token exposed.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={() => runDiag('/admin/diagnostics/whatsapp/waba-subscription', setSubLoading, setSubResult, setSubError)}
            disabled={subLoading} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="wa-sub-run-btn">
            {subLoading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking…</>) : (<><ListChecks className="h-4 w-4 mr-2" /> Check WABA Subscription</>)}
          </Button>
          {subError && (<div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="wa-sub-error"><ShieldAlert className="h-5 w-5 text-red-500 mt-0.5" /><div className="text-sm text-red-700">{subError}</div></div>)}
          <div className="pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-500 mb-2">
              Administrative action — <span className="font-mono">POST /&#123;WABA_ID&#125;/subscribed_apps</span>.
              Subscribes the configured TreatX app to the configured WABA using the existing server-side Meta
              configuration. Runs only when you click.
            </p>
            <Button onClick={subscribeApp} disabled={subscribeLoading}
              className="w-full bg-violet-700 hover:bg-violet-800" data-testid="wa-sub-subscribe-btn">
              {subscribeLoading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Subscribing…</>) : (<><ShieldCheck className="h-4 w-4 mr-2" /> Subscribe TreatX App</>)}
            </Button>
            {subscribeError && (<div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="wa-sub-subscribe-error"><ShieldAlert className="h-5 w-5 text-red-500 mt-0.5" /><div className="text-sm text-red-700">{subscribeError}</div></div>)}
            {subscribeResult && (
              <dl className="mt-3 grid grid-cols-3 gap-y-1 text-sm p-4 rounded-lg border bg-slate-50" data-testid="wa-sub-subscribe-result">
                <dt className={FIELD}>Result</dt>
                <dd className="col-span-2 font-mono" data-testid="wa-sub-subscribe-status">{subscribeResult.success ? 'SUBSCRIBED' : 'FAILED'}</dd>
                <dt className={FIELD}>HTTP</dt><dd className="col-span-2 font-mono">{String(subscribeResult.http_status ?? '—')}</dd>
                <dt className={FIELD}>Endpoint</dt><dd className="col-span-2 font-mono break-all">{subscribeResult.endpoint || '—'}</dd>
                {subscribeResult.meta_error && (<><dt className={FIELD}>Error</dt><dd className="col-span-2 text-red-700">{subscribeResult.meta_error.code}: {subscribeResult.meta_error.message}</dd></>)}
              </dl>
            )}
          </div>
          {subResult && (
            <div className="text-sm p-4 rounded-lg border bg-slate-50" data-testid="wa-sub-result">
              <dl className="grid grid-cols-3 gap-y-1">
                <dt className={FIELD}>HTTP</dt><dd className="col-span-2 font-mono" data-testid="wa-sub-http">{String(subResult.http_status ?? '—')}</dd>
                <dt className={FIELD}>WABA</dt><dd className="col-span-2 font-mono">{subResult.waba_masked}</dd>
                <dt className={FIELD}>Subscribed apps</dt><dd className="col-span-2 font-mono">{String(subResult.subscribed_app_count ?? 0)}</dd>
              </dl>
              {(subResult.subscribed_apps || []).map((s, i) => (
                <div key={i} className="mt-2 p-2 rounded bg-white border" data-testid={`wa-sub-app-${i}`}>
                  <div className="font-mono text-xs">{s.name || '—'} ({s.id || '—'})</div>
                  <div className="text-xs text-slate-500 break-all">{Array.isArray(s.subscribed_fields) ? s.subscribed_fields.join(', ') : (s.subscribed_fields || '—')}</div>
                </div>
              ))}
              {subResult.meta_error && (<div className="mt-2 text-red-700">{subResult.meta_error.code}: {subResult.meta_error.message}</div>)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
